/**
 * Step 13 — Estimate Summary, single-price, Xactimate export + one-tap PDF Preview.
 */
import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Receipt, FileDown, ExternalLink, Loader2, Check, DollarSign } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Step13Estimate({ workflow, request, requestId, onAdvance }) {
  const init = workflow?.step_13 || {};
  const [lineItems, setLineItems] = useState(init.line_items || []);
  const [total, setTotal] = useState(init.total_price || 0);
  const [notes, setNotes] = useState(init.final_notes || "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (lineItems.length === 0) {
      (async () => {
        setLoading(true);
        try {
          const { data } = await API.get(`/inspection/${requestId}/step/13/auto-summary`);
          setLineItems(data.line_items || []);
          if (data.current_total) setTotal(data.current_total);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const previewPdf = async () => {
    setPreviewing(true);
    try {
      // Open in new tab/window — backend returns the PDF inline
      const url = `${API_BASE}/api/inspection/${requestId}/step/13/preview-pdf`;
      window.open(url, "_blank");
      toast.success("Estimate PDF preview opened");
    } catch (err) { toast.error("Could not preview"); }
    finally { setPreviewing(false); }
  };

  const exportXactimate = async () => {
    if (!total || total <= 0) return toast.error("Enter a total estimate first");
    setExporting(true);
    try {
      await API.post(`/inspection/${requestId}/step/13/xactimate-export`);
      toast.success("Queued for estimating-software export");
    } catch (err) { toast.error(err?.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };

  const save = async () => {
    if (!total || total <= 0) return toast.error("Enter a total estimate (must be > $0)");
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/13/save`, {
        total_price: parseFloat(total),
        final_notes: notes,
        line_items: lineItems,
      });
      toast.success("Estimate saved");
      onAdvance(14);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const updateItem = (i, v) => setLineItems(arr => arr.map((x, idx) => idx === i ? v : x));
  const removeItem = (i) => setLineItems(arr => arr.filter((_, idx) => idx !== i));
  const addItem = () => setLineItems(arr => [...arr, ""]);

  return (
    <div className="space-y-4" data-testid="step-13-form">
      <div className="flex items-center gap-2">
        <Receipt className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 13 · Estimate Summary</h2>
      </div>

      {/* One-tap PDF preview */}
      <Button onClick={previewPdf} disabled={previewing}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold"
              data-testid="step-13-preview-pdf-btn">
        {previewing ? <Loader2 className="w-4 h-4 animate-spin" />
          : <><FileDown className="w-4 h-4 mr-1" /> Generate Estimate PDF Preview</>}
      </Button>

      {/* Line items (rolled up) */}
      <Card><CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Itemized Scope</p>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
        </div>
        <div className="space-y-1.5">
          {lineItems.map((li, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={li} onChange={e => updateItem(i, e.target.value)}
                     className="h-9 text-xs flex-1" data-testid={`step-13-li-${i}`} />
              <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => removeItem(i)}
                      data-testid={`step-13-li-remove-${i}`}>×</Button>
            </div>
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={addItem} className="mt-2" data-testid="step-13-li-add">+ Add line item</Button>
      </CardContent></Card>

      {/* Single price */}
      <Card><CardContent className="pt-4 space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Total Estimate (single price)</p>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input type="number" step="0.01" value={total}
                 onChange={e => setTotal(e.target.value)}
                 placeholder="0.00"
                 className="pl-9 h-14 text-2xl font-bold text-right pr-3"
                 data-testid="step-13-total-input" />
        </div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full p-2 text-xs border rounded h-20"
                  placeholder="Final notes (optional)" data-testid="step-13-notes" />
      </CardContent></Card>

      {/* External estimating-software export (vendor-neutral text per Item 9) */}
      <Button onClick={exportXactimate} disabled={exporting} variant="outline" className="w-full h-11"
              data-testid="step-13-xactimate-btn">
        {exporting ? <Loader2 className="w-4 h-4 animate-spin" />
          : <><ExternalLink className="w-4 h-4 mr-1" /> Export to estimating software</>}
      </Button>

      <Button onClick={save} disabled={saving} className="w-full h-12 bg-zinc-900 text-white" data-testid="step-13-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save Estimate & Continue</>}
      </Button>
    </div>
  );
}
